import { Circle, CircleCheck, CircleHelp, Clock, MonitorX, type LucideIcon } from 'lucide-react';

/**
 * Screen-Health verdict — vendored (by copy) from scio3 `mdm-devices/utils/screen-health.ts` +
 * `verdict-display.ts`, fed from the testing drawer's already-resolved signals instead of an
 * MdmDevice. Precedence for managed devices: offline → screen-off → content-expired → playing-ok;
 * a `none` device reports no glass signals, so online collapses to playing-ok.
 */
export type ScreenHealthVerdict = 'offline' | 'screen-off' | 'expired' | 'playing-ok' | 'unknown';

export function getScreenHealthVerdict(input: {
  /** App-axis connectivity; undefined while nothing is known yet. */
  appOnline: boolean | undefined;
  managementCapability: string | null | undefined;
  hdmiActive: boolean | null | undefined;
  contentExpired: boolean | null | undefined;
}): ScreenHealthVerdict {
  if (input.appOnline === undefined) return 'unknown';
  if (!input.appOnline) return 'offline';
  const cap = input.managementCapability;
  if (!cap || cap === 'none') return 'playing-ok';
  if (input.hdmiActive === false) return 'screen-off';
  if (input.contentExpired) return 'expired';
  return 'playing-ok';
}

export interface VerdictDisplay {
  label: string;
  Icon: LucideIcon;
  toneClass: string;
}

/** Colorblind-safe: every state carries an icon + label + tone (never color alone). */
export const VERDICT_DISPLAY: Record<ScreenHealthVerdict, VerdictDisplay> = {
  'playing-ok': { label: 'Playing OK', Icon: CircleCheck, toneClass: 'text-teal-700 dark:text-teal-400' },
  'screen-off': { label: 'Screen off', Icon: MonitorX, toneClass: 'text-amber-700 dark:text-amber-500' },
  expired: { label: 'Content expired', Icon: Clock, toneClass: 'text-amber-700 dark:text-amber-500' },
  offline: { label: 'Offline', Icon: Circle, toneClass: 'text-muted-foreground' },
  unknown: { label: 'Unknown', Icon: CircleHelp, toneClass: 'text-muted-foreground' },
};
